import { useSyncExternalStore } from "react";

export type RealtimeEventType =
  | "stream.ready"
  | "shipment-reminder.updated"
  | "design.job.updated"
  | "listing-draft.updated"
  | "amazon-import.updated"
  | "marketplace.push.updated"
  | "background-job.updated";

export interface RealtimeEvent {
  id: string;
  type: RealtimeEventType;
  occurredAt: string;
  entityType: string;
  entityId: string;
  payload: Record<string, unknown>;
}

export const REALTIME_RECOVERY_INTERVAL_MS = 60_000;

export type RealtimeConnectionState = "disconnected" | "connecting" | "connected";

type RealtimeEventListener = (event: RealtimeEvent) => void;

const listeners = new Set<RealtimeEventListener>();
const connectionListeners = new Set<() => void>();
let connectionState: RealtimeConnectionState = "disconnected";

export function emitRealtimeEvent(event: RealtimeEvent): void {
  for (const listener of listeners) listener(event);
}

export function subscribeRealtimeEvents(listener: RealtimeEventListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setRealtimeConnectionState(next: RealtimeConnectionState): void {
  if (next === connectionState) return;
  connectionState = next;
  for (const listener of connectionListeners) listener();
}

export function getRealtimeConnectionState(): RealtimeConnectionState {
  return connectionState;
}

export function recoveryIntervalForRealtimeState(
  state: RealtimeConnectionState,
  required = true,
): number | false {
  return required && state !== "connected" ? REALTIME_RECOVERY_INTERVAL_MS : false;
}

function subscribeRealtimeConnection(listener: () => void): () => void {
  connectionListeners.add(listener);
  return () => connectionListeners.delete(listener);
}

/**
 * SSE is the primary update path. REST recovery is enabled only while the
 * stream is unavailable, so healthy sessions create no background poll load.
 */
export function useRealtimeRecoveryInterval(required = true): number | false {
  const state = useSyncExternalStore(
    subscribeRealtimeConnection,
    getRealtimeConnectionState,
    () => "disconnected" as const,
  );
  return recoveryIntervalForRealtimeState(state, required);
}

export function isQueueRealtimeEvent(
  event: RealtimeEvent,
  queues: readonly string[],
  entityIds: readonly string[] = [],
): boolean {
  if (event.type !== "background-job.updated") return false;
  const queue = typeof event.payload.queue === "string" ? event.payload.queue : "";
  if (!queues.includes(queue)) return false;
  return entityIds.length === 0 || entityIds.includes(event.entityId);
}

export const realtimeEventInternals = { listeners, connectionListeners };
